import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import { getUserCertificates } from "../../services/certificateService";
import { Certificate } from "../../types/certificate";
import { ArrowLeft } from "lucide-react";

export default function CertificateGallery() {
  const { user } = useAuthContext();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCertificates = async () => {
      if (!user) return;

      try {
        const data = await getUserCertificates(user.uid);
        setCertificates(data);
      } catch (err) {
        console.error("Error fetching certificates:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificates();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textLight">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold transition-colors">
          <ArrowLeft className="w-5 h-5" />
          กลับไปหน้าหลัก
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-textDark mb-2">
            🏆 ใบเซอร์ติฟิเคตของฉัน
          </h1>
          <p className="text-textLight">
            ใบเซอร์ติฟิเคตทั้งหมดที่คุณได้รับจากการเรียนจบคอร์ส
          </p>
        </div>

        {/* Stats */}
        {certificates.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-sm text-textLight mb-2">ใบเซอร์ติฟิเคตทั้งหมด</div>
              <div className="text-3xl font-bold text-primary">{certificates.length}</div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-sm text-textLight mb-2">เดือนนี้</div>
              <div className="text-3xl font-bold text-green-500">
                {
                  certificates.filter((cert) => {
                    const issueDate = new Date(cert.issueDate);
                    const now = new Date();
                    return (
                      issueDate.getMonth() === now.getMonth() &&
                      issueDate.getFullYear() === now.getFullYear()
                    );
                  }).length
                }
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-sm text-textLight mb-2">คอร์สที่เรียนจบ</div>
              <div className="text-3xl font-bold text-blue-500">
                {new Set(certificates.map((cert) => cert.courseId)).size}
              </div>
            </div>
          </div>
        )}

        {/* Certificates Grid */}
        {certificates.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="text-6xl mb-4">🎓</div>
            <h2 className="text-2xl font-bold text-textDark mb-2">
              ยังไม่มีใบเซอร์ติฟิเคต
            </h2>
            <p className="text-textLight mb-6">
              เมื่อคุณเรียนจบคอร์สจะได้รับใบเซอร์ติฟิเคตที่นี่
            </p>
            <Link to="/my-courses">
              <button className="bg-gradient-primary text-white px-8 py-3 rounded-xl font-bold hover:shadow-xl transition-all">
                ดูคอร์สของฉัน
              </button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificates.map((cert) => (
              <Link
                key={cert.id}
                to={`/certificates/${cert.id}`}
                className="group"
              >
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  {/* Certificate Preview */}
                  <div className="bg-gradient-to-br from-blue-500 to-pink-500 p-8 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                    
                    <div className="relative z-10">
                      <div className="text-xs opacity-80 mb-2">CERTIFICATE OF COMPLETION</div>
                      <div className="text-2xl font-bold mb-4 line-clamp-2">
                        {cert.courseTitle}
                      </div>
                      <div className="text-sm opacity-90">
                        {cert.userName}
                      </div>
                    </div>
                  </div>

                  {/* Certificate Info */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-textLight">ออกโดย</span>
                      <span className="text-sm font-semibold text-textDark">
                        {cert.instructorName}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-textLight">วันที่ออก</span>
                      <span className="text-sm text-textDark">
                        {new Date(cert.issueDate).toLocaleDateString("th-TH")}
                      </span>
                    </div>

                    {cert.grade && (
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-textLight">เกรด</span>
                        <span className="text-sm font-bold text-green-500">
                          {cert.grade}
                        </span>
                      </div>
                    )}

                    <div className="pt-3 border-t border-gray-100">
                      <div className="text-xs text-textLight mb-1">
                        เลขที่ใบเซอร์ติฟิเคต
                      </div>
                      <div className="text-xs font-mono text-textDark">
                        {cert.certificateNumber}
                      </div>
                    </div>

                    <button className="w-full mt-4 bg-gradient-primary text-white py-2 rounded-xl font-semibold text-sm group-hover:shadow-lg transition-all">
                      ดูใบเซอร์ติฟิเคต
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
